import { FC, useEffect, useMemo, useState, useCallback, useRef } from "react";
import { MenuProps } from "../../types";
import { Theme } from "../../../types";
import { megaMenuPanel, megaMenuColumn } from "../../styles";
import MegaMenuItem from "./Item";
import MegaMenuGroupTitle from "./GroupTitle";
import MegaMenuWidgetSlot from "./WidgetSlot";
import clsx from "clsx";

interface MegaMenuPanelProps {
  /** 菜单数据 */
  menus: MenuProps;
  /** 当前主题 */
  theme: Theme;
  /** 国际化 locale 前缀，如 "/en" */
  localeLink: string;
  /** 当前 locale，如 "en"、"kr" */
  locale: string;
  /** 路由导航回调 */
  onNavigate: (item: MenuProps, e: React.MouseEvent<HTMLAnchorElement>) => void;
  /** 面板是否打开（用于在关闭时重置内部状态） */
  open: boolean;
  /** 额外的 className（用于位置控制） */
  className?: string;
  /** ref 转发 */
  dropdownRef?: React.Ref<HTMLDivElement>;
  /** 额外 style（用于动态定位） */
  style?: React.CSSProperties;
}

/**
 * MegaMenuPanel — 三级菜单面板（通用）
 *
 * 交互流程：
 * 1. hover 一级菜单项 → 打开此面板，**仅显示左侧菜单列**
 * 2. 在左侧菜单列中 hover `isPrimary` 项 → **右侧 widget 列滑入展开**
 * 3. hover 非 isPrimary 项 → 右侧 widget 列收起
 *
 * 数据格式：
 * 1. `items` — 单列下拉（如 Buy Crypto、Earn）
 * 2. `more` — 多列 Mega Menu（如 Spot、Futures、More）
 *    - 数组格式 `[{ trading: [...], explore: [...] }, { __widget__: [...] }]`
 *    - 对象格式 `{ trading: [...] }` → 向后兼容，转为单列
 *    - `__widget__` key → 渲染为右列 widget，仅在 hover isPrimary 项时展示
 */
const MegaMenuPanel: FC<MegaMenuPanelProps> = ({
  menus,
  theme,
  localeLink,
  locale,
  onNavigate,
  open,
  className,
  dropdownRef,
  style
}) => {
  // ── hover 跟踪：保存完整 item，用于传递上下文给 Widget ──
  const [hoveredItem, setHoveredItem] = useState<MenuProps | null>(null);
  // 是否已经发生过二级 hover 交互：一旦用户主动 hover（包括无内容项），就不再自动默认选中
  const userInteractedRef = useRef(false);

  // 面板关闭或切换一级菜单时，重置二级 hover 状态，保证每次进入是全新的初始态
  useEffect(() => {
    if (!open) {
      setHoveredItem(null);
      userInteractedRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    // 一级菜单切换（menus 变化）时也重置
    setHoveredItem(null);
    userInteractedRef.current = false;
  }, [menus.label]);

  // 标准化 more 为数组列格式
  const columns = useMemo(() => {
    if (!menus.more) return null;

    if (Array.isArray(menus.more)) {
      return menus.more;
    }

    // 对象格式 → 转为单列（向后兼容）
    return [menus.more as { [key: string]: MenuProps[] }];
  }, [menus.more]);

  // 从 columns 中提取菜单级 widget 配置（如果有）
  const menuWidgetConfig = useMemo(() => {
    if (!columns) return null;
    for (const col of columns) {
      const widgetGroup = col["__widget__"];
      if (
        widgetGroup &&
        Array.isArray(widgetGroup) &&
        widgetGroup.length > 0 &&
        widgetGroup[0]?.widgetType
      ) {
        return widgetGroup[0].widgetType as "searchMarket" | "leaderboard" | "botProducts";
      }
    }
    return null;
  }, [columns]);

  // 过滤出非 widget 的菜单列
  const menuColumns = useMemo(() => {
    if (!columns) return null;
    return columns.filter((col) => !col["__widget__"]);
  }, [columns]);

  const isMultiColumn = !!menuColumns;
  const hasItems = !!menus.items?.length;

  // 实际生效的 widget 类型：item 级别覆盖 > 菜单级别配置
  const effectiveWidgetType = hoveredItem?.widgetType || menuWidgetConfig;

  // 右侧 widget 是否展开：hover 了 isPrimary 项 且 有可用的 widget 配置
  const showWidget = !!hoveredItem && !!effectiveWidgetType;

  // 菜单级 widget 常驻渲染（避免 hover 切换二级项导致重复 unmount/mount → 重新请求 + 骨架闪烁）。
  // 仅对菜单级配置生效；item 级别覆盖（如 Bots → botProducts）仍按需渲染。
  const persistMenuWidget = !!menuWidgetConfig;
  const isShowingMenuWidget = showWidget && effectiveWidgetType === menuWidgetConfig;

  /**
   * 默认展开逻辑：
   * 一级菜单 hover 打开面板时，默认选中（hover）第一个“有内容”的二级菜单项。
   *
   * “有内容”= isPrimary 且存在可用 widget（item.widgetType 或菜单级 widget 配置）。
   * 这样可以避免默认选中一个不会展开右侧内容的条目。
   */
  const defaultPrimaryItem = useMemo(() => {
    if (!menuColumns) return null;
    const fallbackWidgetType = menuWidgetConfig;

    for (const column of menuColumns) {
      for (const groupKey of Object.keys(column)) {
        const items = column[groupKey];
        if (!Array.isArray(items) || items.length === 0) continue;
        const found = items.find(
          (item) => !!item.isPrimary && !!(item.widgetType || fallbackWidgetType)
        );
        if (found) return found;
      }
    }
    return null;
  }, [menuColumns, menuWidgetConfig]);

  useEffect(() => {
    if (!open) return;
    if (hoveredItem) return;
    if (userInteractedRef.current) return;
    if (defaultPrimaryItem) {
      setHoveredItem(defaultPrimaryItem);
    }
  }, [open, hoveredItem, defaultPrimaryItem]);

  // Item hover 回调：isPrimary 项触发 widget 展开，否则收起
  const handleItemHover = useCallback((item: MenuProps) => {
    userInteractedRef.current = true;
    if (item.isPrimary) {
      setHoveredItem(item);
    } else {
      setHoveredItem(null);
    }
  }, []);

  return (
    <div
      ref={dropdownRef}
      style={style}
      className={clsx(megaMenuPanel({ mode: theme }), className)}
    >
      {isMultiColumn ? (
        <div
          className={clsx(
            "flex",
            showWidget && "min-h-[360px]",
            menuColumns.length >= 3 && "p-[12px]"
          )}
          style={{ flexDirection: "row" }}
        >
          {menuColumns.map((column, colIdx) => (
            <div
              key={colIdx}
              className={megaMenuColumn({ size: menuColumns.length >= 3 ? "compact" : "default" })}
            >
              {Object.keys(column).map((groupKey) => {
                const items = column[groupKey];
                if (!Array.isArray(items) || items.length === 0) return null;

                return (
                  <div key={groupKey}>
                    <MegaMenuGroupTitle groupKey={groupKey} theme={theme} />
                    {items.map((item) => (
                      <MegaMenuItem
                        key={item.label}
                        item={item}
                        theme={theme}
                        localeLink={localeLink}
                        locale={locale}
                        onNavigate={onNavigate}
                        isActive={item.isPrimary && hoveredItem?.label === item.label}
                        onHover={() => handleItemHover(item)}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          ))}

          {/* 菜单级 widget 常驻渲染（searchMarket / leaderboard 都受益）：
              避免 hover 切换二级项导致重复 unmount/mount → 重新请求 + 骨架闪烁。
              SearchMarket 需要 absolute 滚动容器，Leaderboard 用普通容器。 */}
          {persistMenuWidget &&
            menuWidgetConfig &&
            (menuWidgetConfig === "searchMarket" ? (
              <div
                className={clsx("overflow-hidden relative", !isShowingMenuWidget && "hidden")}
                style={{ maxWidth: 360 }}
                aria-hidden={!isShowingMenuWidget}
              >
                <div className="w-[360px] h-0 shrink-0" />
                <div className="absolute inset-0 overflow-hidden">
                  <MegaMenuWidgetSlot
                    widgetType="searchMarket"
                    theme={theme}
                    menuLabel={menus.label}
                    hoveredItem={hoveredItem}
                    active={open}
                  />
                </div>
              </div>
            ) : (
              <div
                className={clsx("overflow-hidden", !isShowingMenuWidget && "hidden")}
                style={{ maxWidth: 360 }}
                aria-hidden={!isShowingMenuWidget}
              >
                <MegaMenuWidgetSlot
                  widgetType={menuWidgetConfig}
                  theme={theme}
                  menuLabel={menus.label}
                  hoveredItem={hoveredItem}
                  active={open}
                />
              </div>
            ))}

          {/* item 级别覆盖的 widget（如 Spot 菜单中 Bots → botProducts）按需渲染 */}
          {showWidget && effectiveWidgetType && effectiveWidgetType !== menuWidgetConfig && (
            <div className="overflow-hidden" style={{ maxWidth: 360 }}>
              <MegaMenuWidgetSlot
                widgetType={effectiveWidgetType}
                theme={theme}
                menuLabel={menus.label}
                hoveredItem={hoveredItem}
                active={open}
              />
            </div>
          )}
        </div>
      ) : hasItems ? (
        // 单列下拉（如 Buy Crypto、Earn）
        <div className={megaMenuColumn()}>
          {menus.items!.map((item, index) => (
            <MegaMenuItem
              key={item.label || index}
              item={item}
              theme={theme}
              localeLink={localeLink}
              locale={locale}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default MegaMenuPanel;
